import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useAccess } from '@/contexts/AccessContext';
import { AlertTriangle, Eye, EyeOff, LogIn, Loader2, Globe } from 'lucide-react';

/**
 * MobileRUHLogin - Forenklet innlogging for mobil RUH-app
 * Med språkbytter (NO/EN)
 */
export default function MobileRUHLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { refreshAccessLevel } = useAccess();
  const { i18n } = useTranslation();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleLanguage = () => {
    const newLang = isNorwegian ? 'en' : 'nb';
    i18n.changeLanguage(newLang);
    localStorage.setItem('systemklar_language', newLang);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      
      if (result.success) {
        refreshAccessLevel();
        navigate('/m/ruh');
      } else {
        setError(result.error || (isNorwegian ? 'Feil e-post eller passord' : 'Wrong email or password'));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(isNorwegian ? 'En feil oppstod' : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col safe-area-top safe-area-bottom" data-testid="mobile-ruh-login">
      {/* Language toggle */}
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 px-3 py-2 bg-slate-800/80 border border-slate-700 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors"
          data-testid="language-toggle"
        >
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">{isNorwegian ? 'EN' : 'NO'}</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Logo - same as main app */}
        <img 
          src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
          alt="Systemklar" 
          className="h-16 mb-6"
        />
        
        <h1 className="text-2xl font-bold text-white mb-1">RUH</h1>
        <p className="text-slate-400 text-center mb-8">
          {isNorwegian ? 'Rapporter uønskede hendelser' : 'Report unwanted incidents'}
        </p>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          {error && (
            <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm text-center">
              {error}
            </div>
          )}

          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-4 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 text-lg"
              placeholder={isNorwegian ? 'E-post' : 'Email'}
              required
              autoComplete="email"
              data-testid="email-input"
            />
          </div>

          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-4 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 text-lg pr-12"
              placeholder={isNorwegian ? 'Passord' : 'Password'}
              required
              autoComplete="current-password"
              data-testid="password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-cyan-500 text-slate-950 rounded-xl font-semibold text-lg hover:bg-cyan-400 disabled:opacity-50 transition-all shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
            data-testid="login-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                {isNorwegian ? 'Logg inn' : 'Sign in'}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 text-center">
        <p className="text-xs text-slate-500">
          Systemklar © 2025
        </p>
      </div>
    </div>
  );
}
